import React, { useEffect, useState } from "react";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, X, MapPin, Clock, Trash2, Check, HelpCircle, ExternalLink, LayoutGrid, List, User as UserIcon, Building2, Tag } from "lucide-react";
import MapPreview from "../components/app/MapPreview";
import LocationPicker from "../components/app/LocationPicker";
import CalendarGrid from "../components/app/CalendarGrid";

const blankT = {
  title: "", description: "", location: "",
  location_lat: null, location_lng: null,
  start_at: "", end_at: "", team: "",
};

export default function CalendarPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [trainings, setTrainings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [children, setChildren] = useState([]);
  const [rsvps, setRsvps] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankT);
  const [err, setErr] = useState("");
  // Mobile-first default: list view is more usable on small screens. We
  // detect viewport width on mount and pick a smart default.
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "list";
    return window.innerWidth < 768 ? "list" : "grid";
  });
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [detail, setDetail] = useState(null);  // training/match clicked from grid

  const load = async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const [tr, ch, m] = await Promise.all([
        api.get("/trainings"),
        api.get("/children"),
        api.get("/matches"),
      ]);
      setTrainings(tr.data || []);
      setChildren(ch.data || []);
      setMatches(m.data || []);
      // Fetch RSVPs in parallel but tolerate per-item failures
      const rsvpEntries = await Promise.allSettled(
        (tr.data || []).map((tt) => api.get(`/trainings/${tt.id}/rsvps`).then((r) => [tt.id, r.data || []]))
      );
      const filled = Object.fromEntries(
        rsvpEntries.filter((p) => p.status === "fulfilled").map((p) => p.value)
      );
      setRsvps(filled);
    } catch (e) {
      setLoadErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
      };
      await api.post("/trainings", payload);
      setForm(blankT);
      setShowForm(false);
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  const sendRsvp = async (trainingId, childId, status) => {
    await api.post(`/trainings/${trainingId}/rsvp`, { child_id: childId, status });
    const r = await api.get(`/trainings/${trainingId}/rsvps`);
    setRsvps((prev) => ({ ...prev, [trainingId]: r.data }));
  };

  const removeT = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/trainings/${id}`);
    load();
  };

  const dateFmt = (iso) =>
    new Date(iso).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", { dateStyle: "medium", timeStyle: "short" });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono mb-1">// {t("trainings")}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">{t("calendar")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex border border-white/10">
              <button
                onClick={() => setView("grid")}
                className={`px-3 py-2 text-xs font-mono tracking-widest flex items-center gap-1.5 ${view === "grid" ? "bg-pitch text-white" : "text-white/60 hover:text-white"}`}
                data-testid="view-grid"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> {lang === "bg" ? "Мрежа" : "Grid"}
              </button>
              <button
                onClick={() => setView("list")}
                className={`px-3 py-2 text-xs font-mono tracking-widest flex items-center gap-1.5 ${view === "list" ? "bg-pitch text-white" : "text-white/60 hover:text-white"}`}
                data-testid="view-list"
              >
                <List className="w-3.5 h-3.5" /> {lang === "bg" ? "Списък" : "List"}
              </button>
            </div>
            {user?.role === "coach" && (
              <button onClick={() => setShowForm(true)} className="btn-primary px-5 py-2.5 flex items-center gap-2" data-testid="btn-add-training">
                <Plus className="w-4 h-4" /> {t("addTraining")}
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="card-sharp p-12 flex items-center justify-center" data-testid="calendar-loading">
            <div className="pitch-loader"><div className="ball" /></div>
          </div>
        )}

        {!loading && loadErr && (
          <div className="card-sharp p-6 text-red-300 font-mono text-sm" data-testid="calendar-load-error">
            {loadErr}
            <button onClick={load} className="ml-3 text-pitch hover:underline">
              {lang === "bg" ? "Опитай отново" : "Retry"}
            </button>
          </div>
        )}

        {!loading && !loadErr && view === "grid" && (
          trainings.length === 0 && matches.length === 0 ? (
            <div className="card-sharp p-12 text-center" data-testid="calendar-grid-empty">
              <Clock className="w-10 h-10 mx-auto mb-3 text-white/20" />
              <div className="font-oswald uppercase text-xl text-white/60 mb-1">{t("noTrainings")}</div>
              <div className="text-xs text-white/35 font-mono">
                {user?.role === "coach"
                  ? (lang === "bg" ? "Натисни + Тренировка, за да създадеш първата си сесия" : "Tap + Training to create your first session")
                  : (lang === "bg" ? "Твоят треньор още не е добавил тренировки" : "Your coach hasn't added trainings yet")}
              </div>
            </div>
          ) : (
            <CalendarGrid
              trainings={trainings}
              matches={matches}
              canDrag={user?.role === "coach"}
              onMoved={load}
              onSelectEvent={(ev) => setDetail(ev)}
              height={typeof window !== "undefined" && window.innerWidth < 768 ? 480 : 640}
            />
          )
        )}

        {!loading && !loadErr && view === "list" && (trainings.length === 0 ? (
          <div className="card-sharp p-12 text-center text-white/30 font-mono text-xs tracking-widest" data-testid="empty-trainings">
            {t("noTrainings")}
          </div>
        ) : (
          <div className="space-y-3" data-testid="calendar-list">
            {trainings.map((tr, i) => {
              const trRsvps = rsvps[tr.id] || [];
              const yes = trRsvps.filter((r) => r.status === "yes").length;
              const no = trRsvps.filter((r) => r.status === "no").length;
              const maybe = trRsvps.filter((r) => r.status === "maybe").length;
              return (
                <div
                  key={tr.id}
                  className="card-sharp interactive p-5 animate-fade-up"
                  style={{ animationDelay: `${i * 40}ms`, opacity: 0 }}
                  data-testid={`training-${tr.id}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
                    <div>
                      <div className="flex flex-col md:flex-row gap-4 md:items-start justify-between mb-3">
                        <div>
                          <div className="font-oswald uppercase text-2xl tracking-tight">{tr.title}</div>
                          <div className="flex flex-wrap gap-3 mt-2 text-white/50 text-sm">
                            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-pitch" /> {dateFmt(tr.start_at)}</span>
                            {tr.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-pitch" /> {tr.location}</span>}
                            {tr.team && <span className="tag">{tr.team}</span>}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {tr.coach_name && (
                              <span className="chip-badge text-[10px]" data-testid={`training-coach-${tr.id}`}>
                                <UserIcon className="w-3 h-3 mr-1 inline-block" /> {tr.coach_name}
                              </span>
                            )}
                            {tr.academy_name && (
                              <span className="chip-badge text-[10px] gold" data-testid={`training-academy-${tr.id}`}>
                                <Building2 className="w-3 h-3 mr-1 inline-block" /> {tr.academy_name}
                              </span>
                            )}
                            {tr.session_type && tr.session_type !== "training" && (
                              <span className="chip-badge text-[10px]">
                                <Tag className="w-3 h-3 mr-1 inline-block" /> {tr.session_type}
                              </span>
                            )}
                          </div>
                          {tr.description && <p className="text-white/60 text-sm mt-2">{tr.description}</p>}
                        </div>
                        {user?.role === "coach" && (
                          <button onClick={() => removeT(tr.id)} className="text-red-400 hover:text-red-300 self-start" data-testid={`delete-training-${tr.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="border-t border-white/5 pt-3 flex flex-wrap gap-2 items-center">
                        <span className="label-mono">{t("rsvp")}:</span>
                        <span className="tag tag-pitch">✓ {yes}</span>
                        <span className="tag text-yellow-400 border-yellow-500/30">? {maybe}</span>
                        <span className="tag text-red-400 border-red-500/30">✕ {no}</span>
                      </div>

                      {user?.role === "parent" && children.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                          {children.map((c) => {
                            const my = trRsvps.find((r) => r.child_id === c.id);
                            return (
                              <div key={c.id} className="flex items-center gap-2 flex-wrap" data-testid={`rsvp-row-${tr.id}-${c.id}`}>
                                <span className="text-sm font-oswald uppercase tracking-tight min-w-[120px]">{c.name}</span>
                                <RsvpButton active={my?.status === "yes"} onClick={() => sendRsvp(tr.id, c.id, "yes")} icon={Check} color="pitch" label={t("attending")} testId={`rsvp-yes-${tr.id}-${c.id}`} />
                                <RsvpButton active={my?.status === "maybe"} onClick={() => sendRsvp(tr.id, c.id, "maybe")} icon={HelpCircle} color="yellow" label={t("maybe")} testId={`rsvp-maybe-${tr.id}-${c.id}`} />
                                <RsvpButton active={my?.status === "no"} onClick={() => sendRsvp(tr.id, c.id, "no")} icon={X} color="red" label={t("notAttending")} testId={`rsvp-no-${tr.id}-${c.id}`} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {tr.location_lat && tr.location_lng && (
                      <div className="lg:w-[260px] space-y-2">
                        <MapPreview lat={tr.location_lat} lng={tr.location_lng} height={140} />
                        <div className="flex gap-2">
                          <a
                            href={`https://www.openstreetmap.org/?mlat=${tr.location_lat}&mlon=${tr.location_lng}#map=15/${tr.location_lat}/${tr.location_lng}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex-1 btn-secondary py-2 text-xs text-center flex items-center justify-center gap-1.5"
                            data-testid={`open-maps-${tr.id}`}
                          >
                            <ExternalLink className="w-3 h-3" /> {t("openInMaps")}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => setShowForm(false)}>
          <div className="bg-[#0A1628] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A1628]">
              <h2 className="font-oswald uppercase text-2xl">{t("addTraining")}</h2>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3">
              <Field label={t("title")} v={form.title} on={(v) => setForm({ ...form, title: v })} required testId="t-title" />
              <Field label={t("description")} v={form.description} on={(v) => setForm({ ...form, description: v })} testId="t-desc" />
              <Field label={t("location")} v={form.location} on={(v) => setForm({ ...form, location: v })} testId="t-loc" />
              <Field label={t("team")} v={form.team} on={(v) => setForm({ ...form, team: v })} testId="t-team" />
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("startsAt")} type="datetime-local" v={form.start_at} on={(v) => setForm({ ...form, start_at: v })} required testId="t-start" />
                <Field label={t("endsAt")} type="datetime-local" v={form.end_at} on={(v) => setForm({ ...form, end_at: v })} required testId="t-end" />
              </div>
              <div>
                <div className="label-mono mb-2">// {t("pickLocation")}</div>
                <LocationPicker
                  lat={form.location_lat}
                  lng={form.location_lng}
                  onChange={({ lat, lng }) => setForm({ ...form, location_lat: lat, location_lng: lng })}
                />
              </div>
              {err && <div className="text-red-400 text-sm font-mono">{err}</div>}
              <div className="flex gap-3 pt-3 sticky bottom-0 bg-[#0A1628] border-t border-white/10">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 btn-secondary py-3">{t("cancel")}</button>
                <button type="submit" className="flex-1 btn-primary py-3" data-testid="save-training">{t("save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Session detail modal — opened when an event is clicked in grid view */}
      {detail && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-6 animate-fade-up" onClick={() => setDetail(null)} data-testid="event-detail-modal">
          <div className="bg-[#0A1628] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-t-lg md:rounded-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A1628] z-10">
              <div className="flex items-center gap-2 min-w-0">
                {detail.kind === "match" ? <span className="chip-badge amber">MATCH</span> : <span className="chip-badge">TRAINING</span>}
                <h2 className="font-oswald uppercase text-xl tracking-tight truncate">{detail.raw?.title}</h2>
              </div>
              <button onClick={() => setDetail(null)} className="p-1.5 hover:bg-white/5 rounded-md transition-colors" data-testid="close-event-detail">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Stat icon={Clock} label={t("startsAt")} value={detail.start.toLocaleString(lang === "bg" ? "bg-BG" : "en-US", { dateStyle: "medium", timeStyle: "short" })} />
                <Stat icon={Clock} label={t("endsAt")} value={detail.end.toLocaleString(lang === "bg" ? "bg-BG" : "en-US", { dateStyle: "medium", timeStyle: "short" })} />
                {detail.raw?.location && <Stat icon={MapPin} label={t("location")} value={detail.raw.location} colSpan />}
                {detail.raw?.coach_name && <Stat icon={UserIcon} label={lang === "bg" ? "Треньор" : "Coach"} value={detail.raw.coach_name} />}
                {detail.raw?.academy_name && <Stat icon={Building2} label={lang === "bg" ? "Академия" : "Academy"} value={detail.raw.academy_name} />}
                {detail.raw?.session_type && detail.raw.session_type !== "training" && (
                  <Stat icon={Tag} label={lang === "bg" ? "Тип" : "Type"} value={detail.raw.session_type} />
                )}
                {detail.raw?.team && <Stat icon={HelpCircle} label={t("team")} value={detail.raw.team} />}
              </div>
              {detail.raw?.description && (
                <div>
                  <div className="label-mono mb-1.5">// {t("description")}</div>
                  <p className="text-sm text-white/70 whitespace-pre-wrap">{detail.raw.description}</p>
                </div>
              )}
              {detail.raw?.location_lat != null && detail.raw?.location_lng != null && (
                <div>
                  <div className="label-mono mb-1.5">// {t("location")}</div>
                  <MapPreview lat={detail.raw.location_lat} lng={detail.raw.location_lng} height={180} />
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${detail.raw.location_lat}&mlon=${detail.raw.location_lng}#map=15/${detail.raw.location_lat}/${detail.raw.location_lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2 btn-secondary py-2 px-3 text-xs inline-flex items-center gap-1.5"
                  >
                    <ExternalLink className="w-3 h-3" /> {t("openInMaps")}
                  </a>
                </div>
              )}
              {user?.role === "parent" && detail.kind === "training" && children.length > 0 && (
                <div className="border-t border-white/8 pt-4">
                  <div className="label-mono mb-2">// {t("rsvp")}</div>
                  <div className="space-y-2">
                    {children.map((c) => {
                      const trRsvps = rsvps[detail.raw.id] || [];
                      const my = trRsvps.find((r) => r.child_id === c.id);
                      return (
                        <div key={c.id} className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-oswald uppercase tracking-tight min-w-[100px]">{c.name}</span>
                          <RsvpButton active={my?.status === "yes"} onClick={() => sendRsvp(detail.raw.id, c.id, "yes")} icon={Check} color="pitch" label={t("attending")} testId={`detail-rsvp-yes-${c.id}`} />
                          <RsvpButton active={my?.status === "maybe"} onClick={() => sendRsvp(detail.raw.id, c.id, "maybe")} icon={HelpCircle} color="yellow" label={t("maybe")} testId={`detail-rsvp-maybe-${c.id}`} />
                          <RsvpButton active={my?.status === "no"} onClick={() => sendRsvp(detail.raw.id, c.id, "no")} icon={X} color="red" label={t("notAttending")} testId={`detail-rsvp-no-${c.id}`} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}

function Stat({ icon: Icon, label, value, colSpan }) {
  return (
    <div className={`border border-white/8 rounded-md p-3 ${colSpan ? "sm:col-span-2" : ""}`}>
      <div className="label-mono flex items-center gap-1.5 mb-1">{Icon && <Icon className="w-3 h-3 text-pitch" />}{label}</div>
      <div className="font-oswald text-sm tracking-tight truncate">{value}</div>
    </div>
  );
}

function RsvpButton({ active, onClick, icon: Icon, color, label, testId }) {
  const colors = {
    pitch: active ? "bg-pitch text-white border-pitch" : "border-white/10 text-white/60 hover:border-pitch hover:text-pitch",
    yellow: active ? "bg-yellow-400 text-black border-yellow-400" : "border-white/10 text-white/60 hover:border-yellow-400 hover:text-yellow-400",
    red: active ? "bg-red-500 text-black border-red-500" : "border-white/10 text-white/60 hover:border-red-500 hover:text-red-400",
  };
  return (
    <button onClick={onClick} className={`flex items-center gap-1.5 px-3 py-1.5 border text-xs font-oswald uppercase tracking-wider transition-colors ${colors[color]}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

function Field({ label, v, on, type = "text", required, testId }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <input
        type={type}
        value={v ?? ""}
        onChange={(e) => on(e.target.value)}
        required={required}
        className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
        data-testid={testId}
      />
    </div>
  );
}
